"use client";

import { useState, useEffect } from "react";
import { useOpenPanel } from "@openpanel/nextjs";
import { trackGAEvent } from "@/lib/analytics";

type Comment = {
  id: string;
  name: string;
  text: string;
  date: string;
};

type CommentsSectionProps = {
  slug: string;
};

export default function CommentsSection({ slug }: CommentsSectionProps) {
  const op = useOpenPanel();
  const [comments, setComments] = useState<Comment[]>([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Load comments from DB on mount
  useEffect(() => {
    async function fetchComments() {
      try {
        setLoading(true);
        const res = await fetch(`/api/comments?slug=${slug}`);
        const result = await res.json();
        if (result.status === "success") {
          setComments(result.data);
        } else {
          console.error("Failed to load comments:", result.message);
        }
      } catch (e) {
        console.error("Failed to fetch comments", e);
      } finally {
        setLoading(false);
      }
    }

    fetchComments();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!name.trim() || !text.trim()) {
      setError("Please fill out both name and comment fields.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch("/api/comments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug,
          name: name.trim(),
          text: text.trim(),
        }),
      });

      const result = await res.json();

      if (res.ok && result.status === "success") {
        setComments((prev) => [result.data, ...prev]);

        // OpenPanel Analytics
        op.track("blog_comment_posted", {
          slug,
          commentLength: text.trim().length,
        });

        // Google Analytics
        trackGAEvent("blog_comment_posted", "engagement", slug, text.trim().length);

        setName("");
        setText("");
        setSuccess(true);
      } else {
        setError(result.message || "Failed to post comment. Please try again.");
      }
    } catch (e) {
      console.error("Error posting comment:", e);
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: 60, paddingTop: 40, borderTop: "1px solid var(--line)" }}>
      <h2 style={{ fontSize: 24, fontWeight: 500, marginBottom: 24, letterSpacing: "-0.01em" }}>
        Discussion ({comments.length})
      </h2>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 640, marginBottom: 48 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label htmlFor="comment-name" style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--fg-dim)" }}>
            Name
          </label>
          <input
            id="comment-name"
            type="text"
            className="input"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label htmlFor="comment-text" style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--fg-dim)" }}>
            Comment
          </label>
          <textarea
            id="comment-text"
            className="input"
            placeholder="Share your thoughts..."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        {error && (
          <p style={{ color: "#ef4444", fontSize: 14 }}>{error}</p>
        )}

        {success && (
          <p style={{ color: "var(--accent)", fontSize: 14 }}>Comment posted successfully!</p>
        )}

        <div>
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Posting..." : "Post Comment"} <span className="arrow">→</span>
          </button>
        </div>
      </form>

      {/* Comments List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 640 }}>
        {loading ? (
          <p style={{ color: "var(--fg-dim)", fontSize: 15, fontStyle: "italic" }}>
            Loading comments...
          </p>
        ) : comments.length === 0 ? (
          <p style={{ color: "var(--fg-dim)", fontSize: 15, fontStyle: "italic" }}>
            No comments yet. Be the first to share your thoughts!
          </p>
        ) : (
          comments.map((c) => {
            const initial = c.name ? c.name.charAt(0).toUpperCase() : "?";
            return (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  gap: 16,
                  padding: 24,
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  background: "var(--bg-2)",
                }}
              >
                <div
                  className="t-avatar"
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    fontSize: 13,
                    background: "linear-gradient(135deg, var(--accent), var(--orb-1))",
                  }}
                >
                  {initial}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 500, color: "var(--fg)" }}>{c.name}</span>
                    <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--fg-faint)" }}>{c.date}</span>
                  </div>
                  <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--fg-dim)", whiteSpace: "pre-line" }}>
                    {c.text}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
